import React, {
  useEffect,
  useState
} from "react";
import _ from "lodash";
import styled, { css } from "styled-components";
import moment from "moment-timezone";
import { $enum } from "ts-enum-util";

import { ProductStatus } from "src/core/products/api/products.api";
import { DateInput } from "src/core/shared/components/Date/DateInput";
import { ProductStatusRowType } from "src/core/products/components/ProductStatuses/FutureProductStatuses";
import {
  Label,
  Select
} from "src/library/components/Forms";
import { Icon } from "src/library/components/Icon";
import { Icons } from "src/library/components/Icon/types";

interface ProductStatusRowProps {
  productId: number;
  localId?: string;
  productStatus?: ProductStatusRowType;
  disabled?: boolean;
  deleteEnabled?: boolean;
  updateFutureStatuses?: (productStatus: ProductStatusRowType) => void;
  onDelete?: (idToDelete: string | number | undefined) => void
}

/**
 * Renders a product status row of inputs: status, start date and end date. Editable based on disabled prop
 */
const ProductStatusRow: React.ReactFCWithChildren<ProductStatusRowProps> = ({
  productId,
  localId,
  productStatus,
  updateFutureStatuses,
  onDelete,
  disabled = false,
  deleteEnabled = false
}) => {
  const [ selectedStatus, setSelectedStatus ] = useState<ProductStatus>();
  const [ startDate, setStartDate ] = useState<Date>();
  const [ endDate, setEndDate ] = useState<Date>();

  useEffect(() => {
    if (productStatus && !selectedStatus) {
      setSelectedStatus(productStatus.status);
      setStartDate(new Date(productStatus.startDate));

      if (productStatus.endDate) {
        setEndDate(new Date(productStatus.endDate));
      }
    }
  }, [ productStatus, selectedStatus ]);

  useEffect(() => {
    if (updateFutureStatuses && startDate && selectedStatus) {
      updateFutureStatuses({
        ...(productStatus || {}),
        localId,
        productId,
        status: selectedStatus,
        startDate,
        endDate
      });
    }
  }, [
    selectedStatus,
    startDate,
    endDate
  ]);

  return (
    <RowWrapper>
      <InputWrapper>
        <Label
          for={`${productId}-status`}
          label="status"
        />

        <Select
          options={_.map($enum(ProductStatus).getValues(), status => ({
            text: _.capitalize(status),
            value: status
          }))}
          name={`${productId}-status`}
          label="Status"
          handleChange={(element: React.ChangeEvent<HTMLInputElement>) => {
            setSelectedStatus(element.target.value as ProductStatus);
          }}
          as="select"
          disabled={disabled}
          defaultValue={productStatus?.status || ProductStatus.Active}
          autoHideLabels
        />
      </InputWrapper>

      <InputWrapper className="dateInput">
        <Label
          for={`${productId}-startDate`}
          label="Start Date"
        />

        <DateInput
          label="Start Date"
          date={startDate}
          setDate={setStartDate}
          minimumDate={new Date()}
          disabled={disabled}
        />
      </InputWrapper>

      {selectedStatus === "suspended" && (
        <InputWrapper className="dateInput">
          <Label
            for={`${productId}-endDate`}
            label="End Date"
          />

          <DateInput
            label="End Date"
            date={endDate}
            setDate={setEndDate}
            minimumDate={startDate || new Date()}
            maximumDate={moment.utc().add(2, "years").toDate()}
            disabled={disabled}
          />
        </InputWrapper>
      )}

      {onDelete && deleteEnabled && (
        <DeleteIconWrapper onClick={() => onDelete(productStatus?.id || localId)}>
          <Icon
            icon={Icons.CLOSE}
            size={35}
          />
        </DeleteIconWrapper>
      )}

    </RowWrapper>
  );
};

const RowWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    padding: 0 0 ${theme.spacing.margins.sm} 0;
    
    .dateInput {
      margin-left: ${theme.spacing.margins.lg}
    }
  `}
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const DeleteIconWrapper = styled.div`
  ${({ theme }) => css`
      display: flex;
      align-items: end;
      margin-left: ${theme.spacing.margins.lg};
      
      svg:hover {
        cursor: pointer;
      }
  `}
`;

export default ProductStatusRow;
